import {
  EnumSurveyQuestionType,
  type Maybe,
  type Multilingual,
} from "gql/generated";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor";
import { isBooleanResponseAnswered } from "./surveyQuestionCheckbox";
import { isSurveyDesignFieldType } from "./surveyQuestionDesign";
import {
  isQuestionVisible,
  isToggleQuestionType,
} from "./surveyQuestionVisibility";

export const INLINE_QUESTIONNAIRE_MAX_QUESTIONS = 6;

type QuestionnaireQuestionLike = {
  _id?: Maybe<string>;
  parentQuestion?: Maybe<string>;
  type?: Maybe<EnumSurveyQuestionType | string>;
  isActive?: Maybe<boolean>;
  required?: Maybe<boolean>;
  options?: Maybe<
    Maybe<{ isActive?: Maybe<boolean>; label?: Maybe<Multilingual> }>[]
  >;
};

type QuestionnaireLike = {
  questions?: Maybe<Maybe<QuestionnaireQuestionLike>[]>;
};

export const getAnswerableQuestionCount = (survey?: Maybe<QuestionnaireLike>) =>
  (survey?.questions ?? []).filter(
    q => q?.isActive && !isSurveyDesignFieldType(q.type),
  ).length;

export const canShowQuestionnaireInline = (survey?: Maybe<QuestionnaireLike>) =>
  getAnswerableQuestionCount(survey) <= INLINE_QUESTIONNAIRE_MAX_QUESTIONS;

/** Swap one survey's answers without dropping responses that belong to another survey. */
export const replaceOwnedQuestionnaireResponses = <
  T extends { questionData?: { _id?: string | null } | null },
>(
  existing: T[],
  ownedQuestionIds: Iterable<string | null | undefined>,
  replacements: T[],
): T[] => {
  const ids = new Set([...ownedQuestionIds].filter((id): id is string => !!id));
  return [
    ...existing.filter(response => !ids.has(response.questionData?._id ?? "")),
    ...replacements,
  ];
};

export const areRequiredQuestionnaireResponsesAnswered = (
  responses: {
    questionData: QuestionnaireQuestionLike;
    response: Maybe<string>[];
  }[],
) =>
  responses.every(({ questionData, response }) => {
    if (isSurveyDesignFieldType(questionData.type)) return true;
    if (!questionData.required) return true;
    if (isToggleQuestionType(questionData.type)) return true;
    if (!isQuestionVisible(questionData, responses)) return true;
    if (questionData.type === EnumSurveyQuestionType.Boolean) {
      return isBooleanResponseAnswered(questionData, response);
    }
    if (questionData.type === EnumSurveyQuestionType.RichText) {
      return !isLexicalHtmlEmpty(response?.[0]);
    }
    return response.length > 0 && !!response[0] && response[0].length > 0;
  });
